import React, { useEffect, useState, useCallback } from 'react';
import { api, type StayDetail, type FolioCharge, type ChargeType, type PaymentItem, type PaymentMethod } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate, fmtClock } from '../lib/format';
import { OperaWindow, OperaSection, OperaField, type SideButton, type ActionStripTab } from './OperaWindow';

interface Props {
  stayId: string;
  onClose: () => void;
  onChanged: () => void;
}

type Mode = 'view' | 'edit' | 'cancel-confirm';
type Section = 'main' | 'folio';

type FolioDialog =
  | { kind: 'none' }
  | { kind: 'addCharge' }
  | { kind: 'voidCharge'; charge: FolioCharge }
  | { kind: 'recordPayment'; mode: 'charge' | 'refund' | 'preauth' }
  | { kind: 'voidPayment'; payment: PaymentItem };

/**
 * Day-of-week label like Opera shows next to dates ("Sunday", "Wednesday").
 * Always rendered in property time.
 */
function dayName(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    weekday: 'long',
    timeZone: 'Australia/Sydney',
  });
}

/**
 * Format "18-25 May" style date range for the title bar.
 */
function shortRange(arrival: string, departure: string): string {
  const a = new Date(arrival);
  const d = new Date(departure);
  const month = (date: Date) => date.toLocaleDateString('en-AU', { month: 'short', timeZone: 'Australia/Sydney' });
  const day = (date: Date) => date.toLocaleDateString('en-AU', { day: 'numeric', timeZone: 'Australia/Sydney' });
  if (month(a) === month(d)) {
    return `${day(a)}–${day(d)} ${month(a)}`;
  }
  return `${day(a)} ${month(a)} – ${day(d)} ${month(d)}`;
}

function statusDisplay(s: string): string {
  switch (s) {
    case 'TENTATIVE':   return 'TENTATIVE';
    case 'CONFIRMED':   return 'CONFIRMED';
    case 'CHECKED_IN':  return 'CHECKED IN';
    case 'CHECKED_OUT': return 'CHECKED OUT';
    case 'CANCELLED':   return 'CANCELLED';
    case 'NO_SHOW':     return 'NO SHOW';
    default:            return s;
  }
}

export function StayDetailPanel({ stayId, onClose, onChanged }: Props) {
  const [data, setData] = useState<StayDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isManager = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const [section, setSection] = useState<Section>('main');
  const [folioDialog, setFolioDialog] = useState<FolioDialog>({ kind: 'none' });

  // Edit form state
  const [editArrival, setEditArrival] = useState('');
  const [editDeparture, setEditDeparture] = useState('');
  const [editRate, setEditRate] = useState('');
  const [editRateType, setEditRateType] = useState<'NIGHTLY' | 'WEEKLY'>('NIGHTLY');
  const [editNotes, setEditNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.stay(stayId);
      setData(d);
      setEditArrival(d.arrivalDate.slice(0, 10));
      setEditDeparture(d.departureDate.slice(0, 10));
      setEditRate(d.nightlyRate);
      setEditRateType(d.rateType);
      setEditNotes(d.notes ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [stayId]);

  useEffect(() => { load(); }, [load]);

  async function doAction(fn: () => Promise<unknown>, confirmText?: string) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleCheckIn() {
    return doAction(() => api.checkIn(stayId), 'Check in this guest now?');
  }
  async function handleCheckOut() {
    return doAction(
      () => api.checkOut(stayId),
      'Check out this guest now?\n\nThe room will be flagged as DIRTY for housekeeping.'
    );
  }
  async function handleCancelSubmit() {
    if (!cancelReason.trim()) { setError('A reason is required to cancel.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.cancel(stayId, cancelReason.trim());
      await load();
      onChanged();
      setMode('view');
      setCancelReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function handleEditSubmit() {
    const rate = parseFloat(editRate);
    if (isNaN(rate) || rate < 0) { setError('Rate must be a non-negative number.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.editStay(stayId, {
        arrivalDate: editArrival,
        departureDate: editDeparture,
        rateType: editRateType,
        nightlyRate: rate,
        notes: editNotes.trim() || null,
      });
      await load();
      onChanged();
      setMode('view');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Loading state — minimal window
  if (loading || !data) {
    return (
      <OperaWindow title="ACCOMMODATION BOOKING" onClose={onClose} size="compact">
        <div className="loading">{error ?? 'Loading…'}</div>
      </OperaWindow>
    );
  }

  const canCheckIn  = data.status === 'CONFIRMED' || data.status === 'TENTATIVE';
  const canCheckOut = data.status === 'CHECKED_IN' || data.status === 'CONFIRMED';
  const canCancel   = data.status === 'CONFIRMED' || data.status === 'TENTATIVE';
  const canEdit     = data.status === 'CONFIRMED' || data.status === 'TENTATIVE';

  const titleText = `RESERVATION — ${data.occupant.workingName} / ${shortRange(data.arrivalDate, data.departureDate)}`;

  // Side buttons depend on the mode
  let sideButtons: SideButton[];
  if (mode === 'edit') {
    sideButtons = [
      { label: 'Save',   onClick: handleEditSubmit, primary: true, disabled: busy },
      { label: 'Cancel', onClick: () => setMode('view'), disabled: busy },
      { label: 'Close',  onClick: onClose },
    ];
  } else if (mode === 'cancel-confirm') {
    sideButtons = [
      { label: 'Confirm', onClick: handleCancelSubmit, danger: true, disabled: busy || !cancelReason.trim() },
      { label: 'Back',    onClick: () => { setMode('view'); setCancelReason(''); }, disabled: busy },
      { label: 'Close',   onClick: onClose },
    ];
  } else {
    sideButtons = [
      canCheckIn  ? { label: 'Check In',  onClick: handleCheckIn,  primary: true, disabled: busy } : null,
      canCheckOut ? { label: 'Check Out', onClick: handleCheckOut, disabled: busy } : null,
      canEdit     ? { label: 'Edit',      onClick: () => setMode('edit'), disabled: busy } : null,
      canCancel   ? { label: 'Cancel',    onClick: () => setMode('cancel-confirm'), danger: true, disabled: busy } : null,
      { label: 'Close', onClick: onClose },
    ].filter(Boolean) as SideButton[];
  }

  // The red action strip — six tabs, only Comments lit up for now
  const actionStrip: ActionStripTab[] = [
    { label: 'Folio',         enabled: true,  active: section === 'folio', onClick: () => setSection(section === 'folio' ? 'main' : 'folio') },
    { label: 'Comments',      enabled: true,  active: false },
    { label: 'Preferences',   enabled: false },
    { label: 'Profile Notes', enabled: false },
    { label: 'No Post',       enabled: false },
    { label: 'History',       enabled: false },
  ];

  return (
    <OperaWindow
      title={titleText}
      status={statusDisplay(data.status)}
      recordId={data.id.slice(-8)}
      onClose={onClose}
      sideButtons={sideButtons}
      actionStrip={actionStrip}
    >
      {error && <div className="modal-error">{error}</div>}

      {mode === 'view' && section === 'main' && (
        <>
          <OperaSection title="Guest">
            <OperaField label="Name">
              <span className="opera-readonly-strong">{data.occupant.workingName}</span>
            </OperaField>
            {data.occupant.phone && (
              <OperaField label="Phone">
                <span>{data.occupant.phone}</span>
              </OperaField>
            )}
          </OperaSection>

          <OperaSection title="Stay Details">
            <OperaField label="Room / Bed">
              <span>
                Room {data.room.number}
                {data.room.displayName && ` (${data.room.displayName})`} · {data.bed.label}
                {' '}
                <span className="opera-field-hint">({data.bed.bedType.toLowerCase()})</span>
              </span>
            </OperaField>
            <OperaField label="Arrival" status={dayName(data.arrivalDate)}>
              <span>{fmtDate(data.arrivalDate)}</span>
            </OperaField>
            <OperaField label="Departure" status={dayName(data.departureDate)}>
              <span>{fmtDate(data.departureDate)}</span>
            </OperaField>
            <OperaField label="Nights">
              <span>{data.nights}</span>
            </OperaField>
            {data.actualCheckIn && (
              <OperaField label="Checked in">
                <span>{fmtClock(data.actualCheckIn)}</span>
              </OperaField>
            )}
            {data.actualCheckOut && (
              <OperaField label="Checked out">
                <span>{fmtClock(data.actualCheckOut)}</span>
              </OperaField>
            )}
          </OperaSection>

          <OperaSection title="Rate Information">
            <OperaField label="Rate type">
              <span>{data.rateType === 'NIGHTLY' ? 'Nightly' : 'Weekly'}</span>
            </OperaField>
            <OperaField label="Nightly rate">
              <span>${data.nightlyRate} AUD</span>
            </OperaField>
            {data.rateType === 'WEEKLY' && data.weeklyRate && (
              <OperaField label="Weekly rate">
                <span>${data.weeklyRate} AUD</span>
              </OperaField>
            )}
          </OperaSection>

          {data.notes && (
            <OperaSection title="Notes">
              <div className="opera-notes-block">{data.notes}</div>
            </OperaSection>
          )}

          <div className="opera-audit">
            Created {fmtClock(data.createdAt)} by {data.createdBy}
            {data.updatedAt !== data.createdAt && ` · last updated ${fmtClock(data.updatedAt)}`}
          </div>
        </>
      )}

      {section === 'folio' && mode === 'view' && (
        <FolioPanel
          detail={data}
          isManager={isManager}
          onReload={load}
          onAddCharge={() => setFolioDialog({ kind: 'addCharge' })}
          onVoidCharge={(c) => setFolioDialog({ kind: 'voidCharge', charge: c })}
          onRecordPayment={(mode) => setFolioDialog({ kind: 'recordPayment', mode })}
          onVoidPayment={(p) => setFolioDialog({ kind: 'voidPayment', payment: p })}
        />
      )}

      {folioDialog.kind === 'addCharge' && (
        <AddChargeDialog
          stayId={data.id}
          isManager={isManager}
          onClose={() => setFolioDialog({ kind: 'none' })}
          onSaved={() => { setFolioDialog({ kind: 'none' }); load(); }}
        />
      )}
      {folioDialog.kind === 'voidCharge' && (
        <VoidChargeDialog
          charge={folioDialog.charge}
          onClose={() => setFolioDialog({ kind: 'none' })}
          onSaved={() => { setFolioDialog({ kind: 'none' }); load(); }}
        />
      )}
      {folioDialog.kind === 'recordPayment' && (
        <RecordPaymentDialog
          stayId={data.id}
          mode={folioDialog.mode}
          onClose={() => setFolioDialog({ kind: 'none' })}
          onSaved={() => { setFolioDialog({ kind: 'none' }); load(); }}
        />
      )}
      {folioDialog.kind === 'voidPayment' && (
        <VoidPaymentDialog
          payment={folioDialog.payment}
          onClose={() => setFolioDialog({ kind: 'none' })}
          onSaved={() => { setFolioDialog({ kind: 'none' }); load(); }}
        />
      )}

      {mode === 'edit' && (
        <OperaSection title="Edit Accommodation Booking">
          <OperaField label="Arrival" required status={dayName(editArrival)}>
            <input type="date" value={editArrival} onChange={e => setEditArrival(e.target.value)} />
          </OperaField>
          <OperaField label="Departure" required status={dayName(editDeparture)}>
            <input type="date" value={editDeparture} onChange={e => setEditDeparture(e.target.value)} />
          </OperaField>
          <OperaField label="Rate type">
            <select value={editRateType} onChange={e => setEditRateType(e.target.value as 'NIGHTLY' | 'WEEKLY')}>
              <option value="NIGHTLY">Nightly</option>
              <option value="WEEKLY">Weekly</option>
            </select>
          </OperaField>
          <OperaField label="Nightly rate" required>
            <input type="number" min="0" step="10" value={editRate} onChange={e => setEditRate(e.target.value)} />
          </OperaField>
          <OperaField label="Notes">
            <textarea rows={3} value={editNotes} onChange={e => setEditNotes(e.target.value)} />
          </OperaField>
        </OperaSection>
      )}

      {mode === 'cancel-confirm' && (
        <OperaSection title="Cancel Accommodation Booking">
          <p style={{ margin: '4px 0 10px', fontSize: 12 }}>
            Cancel <strong>{data.occupant.workingName}</strong>'s stay? The booking will be marked
            CANCELLED but kept in history.
          </p>
          <OperaField label="Reason" required>
            <textarea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
              placeholder="e.g. Guest never confirmed; she changed her mind" />
          </OperaField>
        </OperaSection>
      )}
    </OperaWindow>
  );
}


// ---------------------------------------------------------------------------
// Folio panel + dialogs
// ---------------------------------------------------------------------------

function FolioPanel({
  detail, isManager, onAddCharge, onVoidCharge,
  onRecordPayment, onVoidPayment,
}: {
  detail: StayDetail;
  isManager: boolean;
  onReload: () => void;
  onAddCharge: () => void;
  onVoidCharge: (c: FolioCharge) => void;
  onRecordPayment: (mode: 'charge' | 'refund' | 'preauth') => void;
  onVoidPayment: (p: PaymentItem) => void;
}) {
  const folio = detail.folio;
  if (!folio) {
    return (
      <OperaSection title="Folio">
        <div className="folio-empty">Loading folio…</div>
      </OperaSection>
    );
  }
  const folioLocked = detail.status === 'CHECKED_OUT' || detail.status === 'CANCELLED';

  return (
    <OperaSection title="Folio">
      <div className="folio-header">
        <div className="folio-status">
          {folioLocked ? (
            <span className="folio-locked">Folio is read-only ({detail.status}).</span>
          ) : (
            <span className="folio-open">Folio is open.</span>
          )}
        </div>
        <div className="folio-actions">
          {!folioLocked && (
            <>
              <button type="button" className="btn-primary" onClick={onAddCharge}>+ Add Charge</button>
              <button type="button" className="btn-primary" onClick={() => onRecordPayment('charge')}>+ Take Payment</button>
              <button type="button" className="btn-small" onClick={() => onRecordPayment('preauth')}>+ Preauth</button>
              {isManager && (
                <button type="button" className="btn-small" onClick={() => onRecordPayment('refund')}>+ Refund</button>
              )}
            </>
          )}
        </div>
      </div>

      {folio.charges.length === 0 ? (
        <div className="folio-empty">No charges posted yet.</div>
      ) : (
        <table className="folio-table">
          <thead>
            <tr>
              <th>Posted</th>
              <th>Type</th>
              <th>Description</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th>By</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {folio.charges.map(c => (
              <tr key={c.id} className={c.isVoid ? 'folio-void' : ''}>
                <td><span className={c.isVoid ? 'folio-strike' : ''}>{fmtClock(new Date(c.postedAt))}</span></td>
                <td><span className="folio-type-pill">{c.chargeType}</span></td>
                <td>
                  <div className={c.isVoid ? 'folio-strike' : ''}>{c.description}</div>
                  {c.relatedKey && (
                    <div className={'folio-related ' + (c.isVoid ? 'folio-strike' : '')}>via {c.relatedKey.keyLabel}</div>
                  )}
                  {c.isVoid && c.voidedReason && (
                    <div className="folio-void-reason">VOID: {c.voidedReason} (by {c.voidedBy})</div>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}><span className={c.isVoid ? 'folio-strike' : ''}>${c.amount}</span></td>
                <td><span className={c.isVoid ? 'folio-strike' : ''}>{c.postedBy}</span></td>
                <td style={{ textAlign: 'right' }}>
                  {!c.isVoid && isManager && !folioLocked && (
                    <button type="button" className="btn-small btn-danger" onClick={() => onVoidCharge(c)}>
                      Void
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Payments table */}
      {folio.payments.length > 0 && (
        <>
          <h4 className="folio-section-h">Payments</h4>
          <table className="folio-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Method</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Status</th>
                <th>By</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {folio.payments.map(p => {
                const isTerminal = p.status === 'FAILED' || p.status === 'EXPIRED';
                const isPendingPreauth = p.paymentType === 'PREAUTH' && p.status === 'PENDING';
                return (
                  <tr key={p.id} className={isTerminal ? 'folio-void' : ''}>
                    <td><span className={isTerminal ? 'folio-strike' : ''}>{fmtClock(new Date(p.createdAt))}</span></td>
                    <td><span className={'folio-type-pill ' + (p.paymentType === 'PREAUTH' ? 'pay-preauth' : (p.paymentType === 'REFUND' ? 'pay-refund' : 'pay-charge'))}>{p.paymentType}</span></td>
                    <td><span className={isTerminal ? 'folio-strike' : ''}>{p.processor}{p.processorRef ? ' · ' + p.processorRef : ''}</span></td>
                    <td style={{ textAlign: 'right' }}><span className={isTerminal ? 'folio-strike' : ''}>${p.amount}</span></td>
                    <td>
                      <span className={'pay-status pay-' + p.status.toLowerCase()}>{p.status}</span>
                      {isPendingPreauth && p.authExpiresAt && (
                        <div className="folio-related">expires {fmtDate(new Date(p.authExpiresAt))}</div>
                      )}
                      {isTerminal && p.failureReason && (
                        <div className="folio-void-reason">{p.failureReason}</div>
                      )}
                    </td>
                    <td><span className={isTerminal ? 'folio-strike' : ''}>{p.createdBy}</span></td>
                    <td style={{ textAlign: 'right' }}>
                      {!isTerminal && !folioLocked && (
                        <button type="button" className="btn-small btn-danger" onClick={() => onVoidPayment(p)}>
                          {isPendingPreauth ? 'Release' : 'Void'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* Totals — at the bottom, includes payments and preauth */}
      <table className="folio-table folio-totals-only">
        <tbody>
          <tr className="folio-total-row">
            <td style={{ textAlign: 'right' }}>Charged:</td>
            <td style={{ textAlign: 'right' }}>${folio.totals.charged}</td>
          </tr>
          {parseFloat(folio.totals.voided) > 0 && (
            <tr className="folio-total-row folio-voided-row">
              <td style={{ textAlign: 'right' }}>Voided charges:</td>
              <td style={{ textAlign: 'right' }}>${folio.totals.voided}</td>
            </tr>
          )}
          <tr className="folio-total-row">
            <td style={{ textAlign: 'right' }}>Paid:</td>
            <td style={{ textAlign: 'right' }}>${folio.totals.paid}</td>
          </tr>
          {parseFloat(folio.totals.preauthHeld) > 0 && (
            <tr className="folio-total-row">
              <td style={{ textAlign: 'right' }}>Preauth held:</td>
              <td style={{ textAlign: 'right' }}>${folio.totals.preauthHeld}</td>
            </tr>
          )}
          <tr className="folio-total-row folio-balance-row">
            <td style={{ textAlign: 'right' }}><strong>Balance:</strong></td>
            <td style={{ textAlign: 'right' }}><strong>${folio.totals.balance}</strong></td>
          </tr>
        </tbody>
      </table>
    </OperaSection>
  );
}

const CHARGE_TYPE_OPTIONS: { value: ChargeType; label: string; needsManager?: boolean }[] = [
  { value: 'ROOM',            label: 'Room (nightly)' },
  { value: 'CLEANING',        label: 'Cleaning fee' },
  { value: 'DAMAGE',          label: 'Damage' },
  { value: 'EXTRA',           label: 'Extra / Misc' },
  { value: 'OTHER',           label: 'Other' },
  { value: 'KEY_BOND',        label: 'Key bond' },
  { value: 'BOND_RETURN',     label: 'Bond returned (credit)' },
  { value: 'KEY_REPLACEMENT', label: 'Key replacement' },
  { value: 'DISCOUNT',        label: 'Discount (Manager)', needsManager: true },
  { value: 'WAIVER',          label: 'Waiver / Write-off (Manager)', needsManager: true },
  { value: 'REFUND',          label: 'Refund (Manager)', needsManager: true },
];

function AddChargeDialog({
  stayId, isManager, onClose, onSaved,
}: {
  stayId: string;
  isManager: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [chargeType, setChargeType] = useState<ChargeType>('ROOM');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = CHARGE_TYPE_OPTIONS.filter(o => !o.needsManager || isManager);

  async function handleSave() {
    if (!description.trim()) { setError('Description is required.'); return; }
    const amt = parseFloat(amount);
    if (isNaN(amt)) { setError('Amount must be a number.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.addCharge(stayId, {
        chargeType,
        description: description.trim(),
        amount: amt,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dialog" role="dialog">
        <div className="dialog-titlebar">
          <span>Add Folio Charge</span>
          <button type="button" className="dialog-close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="dialog-body">
          {error && <div className="dialog-error">{error}</div>}
          <div className="dialog-section">
            <label className="dialog-label">Type *</label>
            <select className="dialog-input" value={chargeType} onChange={e => setChargeType(e.target.value as ChargeType)}>
              {options.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Description *</label>
            <input
              className="dialog-input"
              autoFocus
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="e.g. Night 1, replacement towels"
            />
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Amount *</label>
            <input
              className="dialog-input"
              type="number"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
            />
            <div className="dialog-hint">
              Use a positive number for charges, negative for discounts/credits.
            </div>
          </div>
          <div className="dialog-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={busy || !description.trim() || !amount}>
              {busy ? 'Posting…' : 'Post Charge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VoidChargeDialog({
  charge, onClose, onSaved,
}: {
  charge: FolioCharge;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!reason.trim()) { setError('Reason is required.'); return; }
    setBusy(true);
    try {
      await api.voidCharge(charge.id, reason.trim());
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dialog" role="dialog">
        <div className="dialog-titlebar">
          <span>Void Charge: {charge.description}</span>
          <button type="button" className="dialog-close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="dialog-body">
          {error && <div className="dialog-error">{error}</div>}
          <div className="dialog-section">
            <div className="dialog-hint">
              Voiding records an audit trail but does not delete the charge.
              The amount will be excluded from the balance.
            </div>
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Reason *</label>
            <textarea
              className="dialog-input"
              autoFocus
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Posted in error, refunded via bank transfer"
            />
          </div>
          <div className="dialog-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn-danger" onClick={handleSave} disabled={busy || !reason.trim()}>
              {busy ? 'Voiding…' : 'Void Charge'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Payment dialogs
// ---------------------------------------------------------------------------

const PROCESSOR_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH',          label: 'Cash' },
  { value: 'EFTPOS',        label: 'EFTPOS (card terminal)' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer / PayID' },
  { value: 'OTHER',         label: 'Other' },
];

function RecordPaymentDialog({
  stayId, mode, onClose, onSaved,
}: {
  stayId: string;
  mode: 'charge' | 'refund' | 'preauth';
  onClose: () => void;
  onSaved: () => void;
}) {
  const isPreauth = mode === 'preauth';
  const [amount, setAmount] = useState(isPreauth ? '1' : '');
  const [processor, setProcessor] = useState<PaymentMethod>(isPreauth ? 'EFTPOS' : 'CASH');
  const [processorRef, setProcessorRef] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title =
    mode === 'charge'  ? 'Take Payment' :
    mode === 'refund'  ? 'Record Refund (to provider)' :
                         'Record Card Preauth';
  const actionLabel =
    mode === 'charge'  ? 'Take Payment' :
    mode === 'refund'  ? 'Record Refund' :
                         'Record Preauth';

  // For preauth, restrict processor to card methods
  const procOptions = isPreauth
    ? PROCESSOR_OPTIONS.filter(o => o.value === 'EFTPOS' || o.value === 'OTHER')
    : PROCESSOR_OPTIONS;

  async function handleSave() {
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) { setError('Amount must be a positive number.'); return; }
    setBusy(true);
    setError(null);
    try {
      const body = {
        amount: amt,
        processor,
        processorRef: processorRef.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      if (mode === 'charge')  await api.recordCharge(stayId, body);
      if (mode === 'refund')  await api.recordRefund(stayId, body);
      if (mode === 'preauth') await api.recordPreauth(stayId, body);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dialog" role="dialog">
        <div className="dialog-titlebar">
          <span>{title}</span>
          <button type="button" className="dialog-close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="dialog-body">
          {error && <div className="dialog-error">{error}</div>}
          {isPreauth && (
            <div className="dialog-section">
              <div className="dialog-hint">
                Records a card preauth taken on the EFTPOS terminal. No money
                actually moves. The hold expires after 7 days, or release it
                manually with the Release button.
              </div>
            </div>
          )}
          {mode === 'refund' && (
            <div className="dialog-section">
              <div className="dialog-hint">
                Records money paid OUT to the provider (e.g. card refund of
                accrued tips, overpayment correction).
              </div>
            </div>
          )}
          <div className="dialog-section">
            <label className="dialog-label">Amount *</label>
            <input
              className="dialog-input"
              type="number"
              step="0.01"
              min="0.01"
              autoFocus
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Method *</label>
            <select className="dialog-input" value={processor} onChange={e => setProcessor(e.target.value as PaymentMethod)}>
              {procOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Reference (optional)</label>
            <input
              className="dialog-input"
              value={processorRef}
              onChange={e => setProcessorRef(e.target.value)}
              placeholder="e.g. EFTPOS receipt no., bank transfer ref"
            />
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Notes</label>
            <textarea
              className="dialog-input"
              rows={2}
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>
          <div className="dialog-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={handleSave} disabled={busy || !amount}>
              {busy ? 'Recording…' : actionLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VoidPaymentDialog({
  payment, onClose, onSaved,
}: {
  payment: PaymentItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isPreauth = payment.paymentType === 'PREAUTH' && payment.status === 'PENDING';
  const action = isPreauth ? 'Release' : 'Void';
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!reason.trim()) { setError('Reason is required.'); return; }
    setBusy(true);
    try {
      await api.voidPayment(payment.id, reason.trim());
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="dialog-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="dialog" role="dialog">
        <div className="dialog-titlebar">
          <span>{action} Payment: {payment.paymentType} ${payment.amount}</span>
          <button type="button" className="dialog-close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="dialog-body">
          {error && <div className="dialog-error">{error}</div>}
          <div className="dialog-section">
            <div className="dialog-hint">
              {isPreauth
                ? 'Releases the preauth on the card. The hold is removed; no money was ever charged.'
                : 'Voids a completed payment for audit purposes. Does not actually reverse money — that must be done separately (cash returned, card refunded, etc.).'}
            </div>
          </div>
          <div className="dialog-section">
            <label className="dialog-label">Reason *</label>
            <textarea
              className="dialog-input"
              autoFocus
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={isPreauth ? 'e.g. Key returned undamaged' : 'e.g. Posted wrong amount, refunded via bank transfer'}
            />
          </div>
          <div className="dialog-footer">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="button" className="btn-danger" onClick={handleSave} disabled={busy || !reason.trim()}>
              {busy ? (isPreauth ? 'Releasing…' : 'Voiding…') : action}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
